import React, { useContext, useEffect, useState } from 'react';
import { useSnackBar } from '../../../../contexts/snackbar';
import { ExpenseCategory } from '../../../../interfaces/delivery-company/expenses';
import axios from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import GenericTable from '../../../common/generic-table/generic-table';
import {
  Chip,
  CircularProgress,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  TablePagination,
  Typography,
} from '@mui/material';
import {
  Cancel,
  CancelOutlined,
  Check,
  Delete,
  Start,
} from '@mui/icons-material';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import { isMobile, isTablet } from 'react-device-detect';
import { IconWithText } from '../../../common/styles/components';
import { blue, orange, red } from '@mui/material/colors';
import SingleTextFieldWithChip from '../../../common/input-with-chip/input-with-chip';
import { PaginationContext } from '../../../../contexts/pagination-context';
import { DataResources, ResourceActionType, user_has_data_model_access } from '../../../../utils/permissions';

function ManageCategories({ onClose }: { onClose: () => void }) {
  const { showMessage } = useSnackBar();
  const [categories, setCategories] = useState<ExpenseCategory[]>();
  const [categoriesCount, setCategoriesCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const { user, config } = useContext(AuthContext);
  const [pendingCategoryUpdate, setPendingCategoryUpdate] = useState<number>(0);

  const canEdit = user_has_data_model_access(user, DataResources.EXPENSE_CATEGORIES, ResourceActionType.UPDATE)

  const [showDeactivated, setShowDeactivated] = useState(false);

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
    searchColumn: 'Order_Id',
    searchValue: '',
    searchOperation: 'equals',
  });

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
    setController({
      ...controller,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  const toggleActivation = async (category: ExpenseCategory) => {
    setPendingCategoryUpdate(category.id);
    try {
      const endpoint = `${API_ENDPOINT}/v1/expense-categories/${category.id}/${category.is_active ? 'deactivate' : 'activate'}/`;
      await axios.post(endpoint, {}, config);
      showMessage(
        `category ${category.label} ${category.is_active ? 'deactivated' : 'activated'} successfully.`
      );
      if ((category.is_active && showDeactivated) || !category.is_active)
        setCategories(
          categories!.map((old) =>
            old.id == category.id
              ? { ...category, is_active: !category.is_active }
              : old
          )
        );
      else {
        setCategories(categories!.filter((item) => item.id != category.id));
      }
    } catch {
      showMessage(
        `Failed to  ${category.is_active ? 'deactivate' : 'activate'} ${category.label}.`,
        'error'
      );
    } finally {
      setPendingCategoryUpdate(0);
    }
  };

  const updateCategoryLabel = async (
    category: ExpenseCategory,
    new_label: string
  ) => {
    if (new_label.trim() == category.label) {
      return
    }
    if (new_label.trim().length == 0) {
      showMessage("Expense category category can't be empty.", 'error');
      return;
    }
    setPendingCategoryUpdate(category.id);
    try {
      const endpoint = `${API_ENDPOINT}/v1/expense-categories/${category.id}/`;
      await axios.patch(endpoint, { label: new_label }, config);
      showMessage('category updated successfully');
      setCategories(
        categories!.map((item) =>
          item.id == category.id ? { ...category, label: new_label } : item
        )
      );
    } catch {
      showMessage('Failed to update category', 'error');
    } finally {
      setPendingCategoryUpdate(0);
    }
  };

  const fetchcategories = async () => {
    setIsLoading(true);

    try {
      const response = await axios.get<{
        count: number;
        results: ExpenseCategory[];
      }>(
        `${API_ENDPOINT}/v1/expense-categories/?page=${controller.page + 1}&page_size=${controller.rowsPerPage}&include_deactivated=${showDeactivated ? 'true' : 'false'}`,
        config
      );
      setCategories(response.data.results);
      setCategoriesCount(response.data.count);
    } catch {
      showMessage('Failed to get categories, please try again later.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchcategories();
  }, [showDeactivated, JSON.stringify(controller)]);

  return (
    <CustomDialog
      open={true}
      maxWidth="xl"
      fullScreen={(isMobile && !isTablet)}
      fullWidth
      onClose={onClose}
    >
      <DialogTitle>
        <Typography
          variant="h3"
          component="h3"
          sx={{ textAlign: 'center', mt: 1, mb: 1 }}
        >
          Expenses Categories
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Switch
          checked={showDeactivated}
          onChange={() => setShowDeactivated(!showDeactivated)}
        />{' '}
        Show Deactivated categories
        <br />
        <div
          style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
        >
          <Chip
            color="primary"
            label={`${categoriesCount} Categories`}
            variant={'outlined'}
          />
        </div>
        <br />
        <br />
        <GenericTable<ExpenseCategory>
          sourceView="manage-labels"
          items={categories ?? []}
          selectedItems={new Map()}
          isLoading={isLoading}
          fullWidth
          columns={[
            {
              id: 'id',
              header: 'ID',
              display_body: (category) => category.id,
            },
            {
              id: 'category',
              header: 'category',
              display_body: (category) => (
                <SingleTextFieldWithChip
                  disabled={pendingCategoryUpdate > 0 || !canEdit}
                  value={category.label}
                  setValue={(v) => {
                    updateCategoryLabel(category, v);
                  }}
                />
              ),
            },
            {
              id: 'State',
              header: 'Status',
              display_body: (category) => (
                <>
                  {category.is_active ? (
                    <Chip icon={<Check />} label={'Active'} color="success" />
                  ) : (
                    <Chip
                      icon={<Cancel />}
                      label={'Deactivated'}
                      color="secondary"
                    />
                  )}
                </>
              ),
            },
            {
              id: 'actions',
              header: 'Actions',
              display_body: (category) => (
                <>
                  {category.is_active ? (
                    <Chip
                      icon={
                        pendingCategoryUpdate == category.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <CancelOutlined fontSize="small" />
                        )
                      }
                      label={'Deactivate'}
                      disabled={pendingCategoryUpdate > 0 || !canEdit}
                      onClick={() => toggleActivation(category)}
                      style={{ background: red[100], cursor: 'pointer' }}
                    />
                  ) : (
                    <Chip
                      icon={
                        pendingCategoryUpdate == category.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Start fontSize="small" />
                        )
                      }
                      label={'Reactivate'}
                      disabled={pendingCategoryUpdate > 0 || !canEdit}
                      style={{ background: blue[100] }}
                      onClick={() => toggleActivation(category)}
                    />
                  )}
                </>
              ),
            },
          ]}
          getItemUID={(category) => category.id.toString()}
        />
        <TablePagination
          component="div"
          onPageChange={handlePageChange}
          page={controller.page}
          count={categoriesCount}
          rowsPerPage={controller.rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
          labelRowsPerPage=""
          style={{ overflow: 'visible' }}
        />
      </DialogContent>
    </CustomDialog>
  );
}

export default ManageCategories;
